import React from 'react'
import { notification, message } from 'antd'
import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled, WarningFilled } from '@ant-design/icons'
import i18next from 'i18next'

/**
 * Viesti-utility funktiot Ant Designilla
 * TypeScript-tuella ja i18next-käännöksillä
 */

// Määritellään ilmoitustyypit
export enum MessageType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info'
}

// Notification-asetukset
export interface NotificationOptions {
  title?: string
  description?: string
  duration?: number
  placement?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
}

// Message-asetukset
export interface MessageOptions {
  content?: string
  duration?: number
}

// Loading-viestien paluutyyppi
export type MessageHide = () => void

// Latausviestiasetukset Promise-käsittelyä varten
export interface LoadingMessageOptions {
  loadingMessage?: string
  successMessage?: string
  errorMessage?: string
}

// Käännösavaimien hakemiseen käytettävät apufunktiot
const t = (key: string, options?: any) => i18next.t(key, options)

// Notification tyyppiset ilmoitukset (isommat, kulma-ilmoitukset)

/**
 * Näyttää onnistuneen tallennuksen ilmoituksen
 * @param title - Ilmoituksen otsikko (voidaan ohittaa)
 * @param description - Ilmoituksen kuvaus (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 * @param placement - Ilmoituksen sijainti
 */
export const showSuccessNotification = (
  title?: string,
  description?: string,
  duration = 4,
  placement: NotificationOptions['placement'] = 'topRight'
): void => {
  notification.success({
    message: title || (t('notifications:success:title') as string),
    description: description || (t('notifications:success:description') as string),
    placement,
    duration,
    icon: <CheckCircleFilled style={{ color: '#52c41a', fontSize: 20 }} />
  })
}

/**
 * Näyttää virhe-ilmoituksen
 * @param title - Ilmoituksen otsikko (voidaan ohittaa)
 * @param description - Ilmoituksen kuvaus (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 * @param placement - Ilmoituksen sijainti
 */
export const showErrorNotification = (
  title?: string,
  description?: string,
  duration = 4,
  placement: NotificationOptions['placement'] = 'topRight'
): void => {
  notification.error({
    message: title || (t('notifications:error:title') as string),
    description: description || (t('notifications:error:description') as string),
    placement,
    duration,
    icon: <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 20 }} />
  })
}

/**
 * Näyttää varoitusilmoituksen
 * @param title - Ilmoituksen otsikko (voidaan ohittaa)
 * @param description - Ilmoituksen kuvaus (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 * @param placement - Ilmoituksen sijainti
 */
export const showWarningNotification = (
  title?: string,
  description?: string,
  duration = 4,
  placement: NotificationOptions['placement'] = 'topRight'
): void => {
  notification.warning({
    message: title || (t('notifications:warning:title') as string),
    description: description || (t('notifications:warning:description') as string),
    placement,
    duration,
    icon: <WarningFilled style={{ color: '#faad14', fontSize: 20 }} />
  })
}

/**
 * Näyttää info-ilmoituksen
 * @param title - Ilmoituksen otsikko (voidaan ohittaa)
 * @param description - Ilmoituksen kuvaus (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 * @param placement - Ilmoituksen sijainti
 */
export const showInfoNotification = (
  title?: string,
  description?: string,
  duration = 4,
  placement: NotificationOptions['placement'] = 'topRight'
): void => {
  notification.info({
    message: title || (t('notifications:info:title') as string),
    description: description || (t('notifications:info:description') as string),
    placement,
    duration,
    icon: <InfoCircleFilled style={{ color: '#1677ff', fontSize: 20 }} />
  })
}

// Message tyyppiset ilmoitukset (pienemmät keskellä ylhäällä)

/**
 * Näyttää onnistuneen toiminnon viestin
 * @param content - Viestin sisältö (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 */
export const showSuccessMessage = (content?: string, duration = 2): void => {
  message.success({
    content: content || (t('notifications:success:saved') as string),
    duration
  })
}

/**
 * Näyttää onnistuneen poiston viestin
 * @param content - Viestin sisältö (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 */
export const showDeleteSuccessMessage = (content?: string, duration = 2): void => {
  message.success({
    content: content || (t('messages:deleteSuccess') as string),
    duration
  })
}

/**
 * Näyttää onnistuneen lisäyksen viestin
 * @param content - Viestin sisältö (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 */
export const showCreateSuccessMessage = (content?: string, duration = 2): void => {
  message.success({
    content: content || (t('messages:createSuccess') as string),
    duration
  })
}

/**
 * Näyttää virheviestin
 * @param content - Viestin sisältö (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 */
export const showErrorMessage = (content?: string, duration = 2): void => {
  message.error({
    content: content || (t('notifications:error:actionFailed') as string),
    duration
  })
}

/**
 * Näyttää varoitusviestin
 * @param content - Viestin sisältö (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 */
export const showWarningMessage = (content?: string, duration = 2): void => {
  message.warning({
    content: content || (t('notifications:warning:title') as string),
    duration
  })
}

/**
 * Näyttää infoviestin
 * @param content - Viestin sisältö (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 */
export const showInfoMessage = (content?: string, duration = 2): void => {
  message.info({
    content: content || (t('notifications:info:title') as string),
    duration
  })
}

/**
 * Näyttää latausviestin
 * @param content - Viestin sisältö (voidaan ohittaa)
 * @param duration - Kesto sekunteina (0 = ei poistu automaattisesti)
 * @returns MessageHide - Funktio ilmoituksen piilottamiseen
 */
export const showLoadingMessage = (content?: string, duration = 0): MessageHide => {
  return message.loading({
    content: content || (t('notifications:loading:title') as string),
    duration
  })
}

/**
 * Esimerkki miten käyttää latausviestiä, joka päättyy onnistumiseen tai virheeseen
 * @param promise - Promise jota odotetaan
 * @param options - Viestien asetukset
 * @returns Alkuperäisen promisen tulos
 */
// eslint-disable-next-line func-names
export const withLoadingMessage = function <T>(promise: Promise<T>, options: LoadingMessageOptions = {}) {
  const loadingMessage = options.loadingMessage || (t('notifications:loading:saving') as string)
  const hide = showLoadingMessage(loadingMessage)

  return promise
    .then(result => {
      hide()
      showSuccessMessage(options.successMessage || (t('notifications:success:saved') as string))
      return result
    })
    .catch(error => {
      hide()
      const errorMsg = error instanceof Error ? error.message : (t('notifications:error:unknownError') as string)
      showErrorMessage(options.errorMessage || errorMsg)
      throw error
    })
}

/**
 * Yleiskäyttöinen ilmoitusfunktio, joka voi näyttää minkä tahansa ilmoitustyypin
 * @param type - Ilmoituksen tyyppi (success, error, warning, info)
 * @param title - Ilmoituksen otsikko (voidaan ohittaa)
 * @param description - Ilmoituksen kuvaus (voidaan ohittaa)
 * @param duration - Kesto sekunteina
 * @param placement - Ilmoituksen sijainti
 */
export const showNotification = (
  type: MessageType,
  title?: string,
  description?: string,
  duration = 4,
  placement: NotificationOptions['placement'] = 'topRight'
): void => {
  // Oletustekstien hakeminen käännöksistä
  let defaultTitle = ''
  let defaultDescription = ''

  switch (type) {
    case MessageType.SUCCESS:
      defaultTitle = t('notifications:success:title') as string
      defaultDescription = t('notifications:success:description') as string
      break
    case MessageType.ERROR:
      defaultTitle = t('notifications:error:title') as string
      defaultDescription = t('notifications:error:description') as string
      break
    case MessageType.WARNING:
      defaultTitle = t('notifications:warning:title') as string
      defaultDescription = t('notifications:warning:description') as string
      break
    case MessageType.INFO:
      defaultTitle = t('notifications:info:title') as string
      defaultDescription = t('notifications:info:description') as string
      break
    default:
      break
  }

  const icons = {
    [MessageType.SUCCESS]: <CheckCircleFilled style={{ color: '#52c41a', fontSize: 20 }} />,
    [MessageType.ERROR]: <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 20 }} />,
    [MessageType.WARNING]: <WarningFilled style={{ color: '#faad14', fontSize: 20 }} />,
    [MessageType.INFO]: <InfoCircleFilled style={{ color: '#1677ff', fontSize: 20 }} />
  }

  notification[type]({
    message: title || defaultTitle,
    description: description || defaultDescription,
    placement,
    duration,
    icon: icons[type]
  })
}
